import type { DeletedMemberView } from '@leanin/shared';
import { Avatar, Label, type LabelSize } from '@leanin/ui';
import { useTranslations } from '@/i18n/translations';
import { RouterLinkAs } from '@/lib/router-link';
import { memberPath } from '../lib/routes';

// How a person who wrote or appears in content is shown. A member who is no
// longer active arrives from the API as `{ kind: 'deleted' }` (staff still
// get the real member), and every surface renders it the same way: a muted
// "Deleted user", the generic person avatar, and no profile link. A member
// whose name is empty shows as "Lean In member". Row layouts get this through
// `MemberRow`'s `member` prop; layouts that place the name and avatar apart
// (a comment bubble) use the two pieces below.

export type MemberIdentity =
  | {
      readonly kind: 'member';
      readonly id: string;
      readonly name: string;
      readonly photo: string | null;
    }
  | DeletedMemberView;

export function useMemberDisplayName(member: MemberIdentity): string {
  return memberDisplayName(member, useTranslations('Common'));
}

/** `useMemberDisplayName` for a render path that can't call the hook (one
 *  past an early return); pass the `Common` translator. */
export function memberDisplayName(
  member: { readonly kind: 'member'; readonly name: string } | DeletedMemberView,
  tCommon: (key: 'deletedUser' | 'memberFallback') => string,
): string {
  if (member.kind === 'deleted') return tCommon('deletedUser');
  return member.name.length > 0 ? member.name : tCommon('memberFallback');
}

export function MemberAvatar({
  member,
  size,
  decorative = false,
}: {
  member: MemberIdentity;
  size: number;
  /** Set when a visible name beside the avatar already carries the
   *  accessible name. A member's decorative avatar still links to the
   *  profile, hidden from assistive tech and the tab order. */
  decorative?: boolean;
}) {
  const name = useMemberDisplayName(member);
  if (member.kind === 'deleted') {
    return <Avatar alt={name} size={size} decorative={decorative} />;
  }
  return (
    <Avatar
      {...(member.photo !== null ? { src: member.photo } : {})}
      name={name}
      size={size}
      decorative={decorative}
      href={memberPath(member.id)}
      linkAs={RouterLinkAs}
    />
  );
}

export function MemberName({ member, size }: { member: MemberIdentity; size: LabelSize }) {
  const name = useMemberDisplayName(member);
  if (member.kind === 'deleted') {
    return (
      <Label size={size} color="mute">
        {name}
      </Label>
    );
  }
  return (
    <RouterLinkAs
      href={memberPath(member.id)}
      style={{ color: 'inherit', textDecoration: 'none', alignSelf: 'flex-start' }}
    >
      <Label size={size}>{name}</Label>
    </RouterLinkAs>
  );
}
